// Closed registry of UI usage events recorded from the authenticated Bill app.
// One `defineUiEvent` entry in `ACCOUNT_UI_EVENTS` is the whole declaration of an event: the
// wire union, every exported type and the event-name list derive from it (`api/ui-usage.ts`).
// WARNING: NEVER include PII or free text. Every payload field is a uuid, an enum, a bucket
// enum or a bounded integer, so nothing a user typed or a record's contents can reach the
// events table. The `no-pii-in-events` ESLint rule lints the field names in this file.
//
// Names sit under `ui-usage.` (the owning domain's segment), so no `chat.%`-style prefix
// pattern in a BI query can match them by accident. They are not `EventMap` keys: they travel
// on the dedicated UI-event topic (`platform/events/ui-usage-events.ts`) into the same table.
import { z } from 'zod';
import { OAuthProviderSchema } from './api/auth.js';
import { LandingSignupCtaSchema } from './campaign-attribution.js';
import { LedgerKindSchema } from './ledger.js';
import { defineUiEvent } from './ui-usage.registry.js';
/**
 * Every `<Route path>` the Bill app records page views for, except the `*` catch-all, which
 * is recorded as `not-found`: the paths of `authenticatedRoutes` (under `Layout`) and
 * `wizardRoutes` (under `WizardShell`). Account-less routes (login, signup, account
 * selection, activation and the like) are deliberately unlisted, because no account exists
 * to attribute a view to. A closed list rather than a template-shaped regex: a regex would
 * admit a raw path holding a client's name. A new route in either table adds an entry here,
 * which a Bill route-sync test enforces.
 */
export const BILL_ROUTE_TEMPLATES = [
    '/',
    '/dashboard',
    '/clients',
    '/clients/new',
    '/clients/:clientId/edit',
    '/clients/:clientId/statement',
    '/clients/:clientId/trust-statement',
    '/clients/:clientId',
    '/matters/:matterId',
    '/matters/:matterId/statement',
    '/activities',
    '/invoices',
    '/invoices/new',
    '/invoices/:invoiceId',
    '/accounting',
    '/accounting/journal-form',
    '/accounting/accounts/:code',
    '/expenses',
    '/expenses/new',
    '/expenses/:expenseId',
    '/suppliers/new',
    '/suppliers/:supplierId/edit',
    '/suppliers/:supplierId',
    '/emails',
    '/calendar',
    '/settings',
    '/help',
    '/referral',
    '/help/:slug',
    '/bank-accounts',
    '/bank-accounts/:bankAccountId',
    '/bank-accounts/:bankAccountId/statements',
    '/bank-accounts/:bankAccountId/statements/:statementId',
    '/bank-accounts/:bankAccountId/upload',
    '/subscribe',
    '/member-onboarding',
];
export const BillRouteTemplateSchema = z.enum(BILL_ROUTE_TEMPLATES);
/** How many characters a search query held, never the query itself. */
export const QueryLengthBucketSchema = z.enum(['0', '1-2', '3-5', '6-10', '11-20', '21+']);
/** Where a chosen omnibox record sat in the list, bucketed. */
export const PositionBucketSchema = z.enum(['1', '2-3', '4-5', '6+']);
/** How a setup wizard step came to be shown. */
export const UiSetupHowSchema = z.enum(['start', 'forward', 'back', 'resume']);
export const UiSetupWizardSchema = z.enum(['member-onboarding']);
export const UiOmniboxTriggerSchema = z.enum(['click', 'shortcut']);
/** Which kind of "Go to" row was chosen in the omnibox; never which page, tab or section by name. */
export const UiOmniboxDestinationSchema = z.enum([
    'page',
    'settings-tab',
    'settings-section',
    'accounting-tab',
    'accounting-report',
]);
/** What an empty-state click did. */
export const UiEmptyStateActionSchema = z.enum(['next-step', 'ask-bill-suggestion', 'ask-bill-prompt']);
/**
 * Fields on every UI usage event. `clientEventId` is the dedupe key (delivery is
 * at-least-once; the envelope `id` is minted by the server per publish, so it cannot dedupe a
 * client retry). `visitId` is one tab's visit, held in module memory. `clientVersion` is the
 * build's short git sha, `localhost`, or a sha with a dirty suffix - a pattern, never free text.
 */
export const UiEventBase = z.strictObject({
    clientEventId: z.uuid(),
    visitId: z.uuid(),
    seq: z.number().int().min(0).max(100_000),
    viewport: z.enum(['desktop', 'phone']),
    clientVersion: z.string().regex(/^(localhost|[0-9a-f]{7,40}(-dirty-[a-z0-9]{1,8})?)$/),
});
// Id lists the Bill app also holds (tabs, reports, wizard steps, empty-state pages, steps and
// CTAs). Plain `as const` arrays like `BILL_ROUTE_TEMPLATES`, not `z.enum` exports: the app owns
// these ids, and Bill sync tests hold each list to its app counterpart in both directions.
export const UI_SETTINGS_TABS = [
    'practice',
    'billing',
    'team',
    'subscription',
    'profile',
    'notifications',
    'accounting',
    'xero',
    'privacy',
];
export const UI_ACCOUNTING_TABS = ['reports', 'accounts', 'journals', 'assets'];
export const UI_ACCOUNTING_REPORTS = [
    'income-statement',
    'balance-sheet',
    'trust-creditors',
    'trial-balance',
    'cash-flow',
    'cashbook',
    'debtors-age-analysis',
    'creditors-age-analysis',
];
// TODO: the member-onboarding wizard is gone (setup is a chat now), so no current client sends the
// setup.step events. They stay in the contract for bundles already loaded; remove them, and the
// wizard names above, once those have aged out.
export const UI_SETUP_STEPS = ['welcome', 'rate', 'whatsapp', 'email', 'done'];
export const UI_EMPTY_STATE_PAGES = ['activities', 'invoices', 'invoicesEntity', 'emails', 'calendar', 'clients', 'expenses'];
export const UI_EMPTY_STATE_STEPS = ['client', 'matter', 'activity', 'approve', 'invoice', 'inbox', 'calendar', 'expense'];
export const UI_DASHBOARD_PANELS = ['wip', 'outstanding', 'chats'];
export const UI_CTAS = [
    'subscription-banner-renew',
    'subscription-banner-subscribe-again',
    'subscription-banner-trial-subscribe',
    'subscription-banner-inactive-subscribe',
    'chat-usage-limit-upgrade',
];
const wizard = UiSetupWizardSchema;
const emptyStatePage = z.enum(UI_EMPTY_STATE_PAGES);
const emptyStateStep = z.enum(UI_EMPTY_STATE_STEPS);
/**
 * Every event the authenticated Bill app may record. Adding one is one entry here. A payload
 * is `UiEventBase.extend({...})` so the common fields stay closed and every key is linted; a
 * payload that varies by a field is a `z.discriminatedUnion` of such branches.
 */
export const ACCOUNT_UI_EVENTS = [
    defineUiEvent('ui-usage.page.viewed', UiEventBase.extend({
        route: z.union([BillRouteTemplateSchema, z.literal('not-found')]),
        navigation: z.enum(['initial', 'push', 'pop', 'replace']),
    })),
    defineUiEvent('ui-usage.tab.viewed', z.discriminatedUnion('surface', [
        UiEventBase.extend({ surface: z.literal('settings'), tab: z.enum(UI_SETTINGS_TABS) }),
        UiEventBase.extend({ surface: z.literal('accounting'), tab: z.enum(UI_ACCOUNTING_TABS), ledger: LedgerKindSchema }),
        UiEventBase.extend({
            surface: z.literal('accounting-report'),
            tab: z.enum(UI_ACCOUNTING_REPORTS),
            ledger: LedgerKindSchema,
        }),
    ])),
    defineUiEvent('ui-usage.setup.step-viewed', UiEventBase.extend({
        wizard,
        step: z.enum(UI_SETUP_STEPS),
        how: UiSetupHowSchema,
    })),
    defineUiEvent('ui-usage.setup.step-skipped', UiEventBase.extend({ wizard, step: z.enum(UI_SETUP_STEPS) })),
    defineUiEvent('ui-usage.empty-state.shown', UiEventBase.extend({ page: emptyStatePage, nextStep: emptyStateStep })),
    defineUiEvent('ui-usage.empty-state.action-clicked', UiEventBase.extend({
        page: emptyStatePage,
        nextStep: emptyStateStep,
        action: UiEmptyStateActionSchema,
    })),
    // One event per open of a dashboard panel from the chat card's top row, at any width (a phone opens it as a sheet).
    defineUiEvent('ui-usage.dashboard-panel.opened', UiEventBase.extend({ panel: z.enum(UI_DASHBOARD_PANELS) })),
    // One event per open of the bell. `unread` says whether the badge showed anything.
    defineUiEvent('ui-usage.notifications.opened', UiEventBase.extend({ unread: z.enum(['none', 'some']) })),
    defineUiEvent('ui-usage.cta.clicked', UiEventBase.extend({ cta: z.enum(UI_CTAS) })),
    defineUiEvent('ui-usage.omnibox.opened', UiEventBase.extend({ trigger: UiOmniboxTriggerSchema })),
    // One event per open. `position` is the 1-based rank within the chosen row's own group: among
    // record rows for a record, among "Go to" rows for a destination (the pinned Ask row is in
    // neither). `suggested` is the fallback rows shown when the user has no history.
    // `recordsShown` counts record rows only, not "Go to" rows.
    defineUiEvent('ui-usage.omnibox.closed', z.discriminatedUnion('outcome', [
        UiEventBase.extend({
            outcome: z.literal('record-selected'),
            kind: z.enum(['client', 'matter', 'invoice']),
            source: z.enum(['search', 'recent', 'suggested']),
            position: PositionBucketSchema,
            queryLength: QueryLengthBucketSchema,
        }),
        UiEventBase.extend({
            outcome: z.literal('destination-selected'),
            destination: UiOmniboxDestinationSchema,
            position: PositionBucketSchema,
            queryLength: QueryLengthBucketSchema,
        }),
        UiEventBase.extend({
            outcome: z.literal('ask'),
            recordsShown: z.enum(['none', 'some']),
            queryLength: QueryLengthBucketSchema,
        }),
        UiEventBase.extend({
            outcome: z.literal('dismissed'),
            recordsShown: z.enum(['none', 'some']),
            queryLength: QueryLengthBucketSchema,
        }),
    ])),
    defineUiEvent('ui-usage.omnibox.search-returned-empty', UiEventBase.extend({ queryLength: QueryLengthBucketSchema })),
];
/**
 * Fields on every anonymous UI usage event: no identity of any kind, so no `seq` or
 * `clientVersion`, and no `visitId` outside the marketing site's own events
 * (`LandingUiEventBase`). `clientEventId` is the
 * read-time dedupe key. `cta`, `audience` and `channel` are the closed arrival triple derived
 * from the landing URL (`uiArrivalOf`); `none` means the visit carried no such parameter.
 */
export const AnonymousUiEventBase = z.strictObject({
    clientEventId: z.uuid(),
    viewport: z.enum(['desktop', 'phone']),
    cta: z.union([LandingSignupCtaSchema, z.literal('none')]),
    audience: z.enum(['advocates', 'attorneys', 'law-firms', 'other', 'none']),
    channel: z.enum(['paid', 'campaign', 'none']),
});
/**
 * The base of the marketing site's events: the anonymous base plus `visitId`, a random id the
 * site keeps in the tab's session storage, renewed after 30 minutes without an event. It
 * groups one visit's landing events and nothing else: the site never passes it to the app, so
 * the pre-account events there carry none.
 */
export const LandingUiEventBase = AnonymousUiEventBase.extend({ visitId: z.uuid() });
/** A marketing-site page. Legal pages and anything unlisted are `other`. */
export const LandingPageSchema = z.enum([
    'home',
    'advocates',
    'attorneys',
    'law-firms',
    'pricing',
    'about',
    'faq',
    'contact',
    'trust',
    'other',
]);
/**
 * The marketing-site sections a page view can reach, each marked `data-track-section` in the
 * markup. A landing e2e test fails if the markup carries an id missing here.
 */
export const LANDING_SECTIONS = [
    'hero',
    'how-it-works',
    'practices',
    'tour',
    'faq',
    'security',
    'trial',
    'pricing',
    'compare',
];
export const LandingSectionSchema = z.enum(LANDING_SECTIONS);
/** The product tour's features, as `TOUR_CHAPTERS` in the landing site declares them. */
export const LANDING_TOUR_FEATURES = [
    'chat',
    'email',
    'email-expenses',
    'calendar',
    'expenses',
    'clients',
    'invoices',
    'entities',
    'lines',
    'trust',
    'reports',
    'vat',
    'tax',
    'more',
];
export const LandingTourFeatureSchema = z.enum(LANDING_TOUR_FEATURES);
/** Where a non-sign-up link on the marketing site leads. Sign-up buttons are `landing.cta-clicked`. */
export const LandingLinkTargetSchema = z.enum([
    'home',
    'advocates',
    'attorneys',
    'law-firms',
    'pricing',
    'about',
    'faq',
    'contact',
    'trust',
    'legal',
    'sign-in',
    'booking',
    'email',
    'phone',
    'whatsapp',
    'in-page',
    'external',
]);
/** The furthest quarter of the page a view scrolled to: `75` means at least three quarters, short of the end. */
export const LandingScrollDepthSchema = z.enum(['0', '25', '50', '75', '100']);
/** Seconds the page was visible with the visitor active (input within the last 30 seconds), bucketed. */
export const LandingEngagedTimeSchema = z.enum(['0-4', '5-14', '15-29', '30-59', '60-179', '180+']);
/** Where the visitor came from, read from the referring origin only. */
export const LandingReferrerSchema = z.enum(['search', 'internal', 'external', 'none']);
/**
 * Events from before the visitor has an account, on the identity-free channel
 * (`POST /ui-usage-events/anonymous`). Every name starts `ui-usage.anonymous.` and no
 * account name may, so the channel is visible in the type, at the route and in BigQuery.
 * The stored rows carry `context: null`. Landing events may be grouped by their `visitId`;
 * nothing here may ever be joined to another row by anything else.
 * `landing.cta-clicked` carries the clicked button as its base `cta`; elsewhere `cta` is the
 * button the visit arrived from.
 */
export const ANONYMOUS_UI_EVENTS = [
    defineUiEvent('ui-usage.anonymous.page.viewed', AnonymousUiEventBase.extend({ page: z.enum(['signup', 'verify-email-pending', 'select-account', 'onboarding']) })),
    defineUiEvent('ui-usage.anonymous.signup.email-chosen', AnonymousUiEventBase),
    defineUiEvent('ui-usage.anonymous.oauth.started', AnonymousUiEventBase.extend({
        provider: OAuthProviderSchema,
        surface: z.enum(['signup', 'login', 'deep-link', 'invite']),
    })),
    defineUiEvent('ui-usage.anonymous.oauth.returned', AnonymousUiEventBase.extend({
        provider: z.union([OAuthProviderSchema, z.literal('unknown')]),
        outcome: z.enum([
            'registered',
            'signed-in',
            'cancelled',
            'provider-error',
            'email-unverified',
            'rejected',
            'unavailable',
        ]),
    })),
    defineUiEvent('ui-usage.anonymous.form.invalid', z.discriminatedUnion('form', [
        AnonymousUiEventBase.extend({
            form: z.literal('signup'),
            field: z.enum(['name', 'email', 'password', 'confirm-password']),
        }),
        AnonymousUiEventBase.extend({ form: z.literal('onboarding'), field: z.enum(['practice-type', 'name']) }),
    ])),
    defineUiEvent('ui-usage.anonymous.form.failed', AnonymousUiEventBase.extend({
        form: z.enum(['signup', 'verify-email', 'verification-resend', 'onboarding']),
        reason: z.enum(['email-taken', 'rate-limited', 'rejected', 'unavailable']),
    })),
    defineUiEvent('ui-usage.anonymous.verify-email.shown', AnonymousUiEventBase.extend({ state: z.enum(['ready', 'link-dead']) })),
    defineUiEvent('ui-usage.anonymous.invite.shown', AnonymousUiEventBase.extend({ state: z.enum(['open', 'expired', 'invalid']) })),
    defineUiEvent('ui-usage.anonymous.landing.cta-clicked', LandingUiEventBase.extend({ page: LandingPageSchema })),
    defineUiEvent('ui-usage.anonymous.landing.page-viewed', LandingUiEventBase.extend({ page: LandingPageSchema, referrer: LandingReferrerSchema })),
    // One summary per page view, sent when the page is first hidden. `signupClicked` is whether
    // that same view also sent a `landing.cta-clicked`.
    defineUiEvent('ui-usage.anonymous.landing.page-left', LandingUiEventBase.extend({
        page: LandingPageSchema,
        scrollDepth: LandingScrollDepthSchema,
        engagedTime: LandingEngagedTimeSchema,
        sections: z.array(LandingSectionSchema).max(LANDING_SECTIONS.length),
        tour: z.enum(['absent', 'unplayed', 'autoplayed', 'picked']),
        signupClicked: z.boolean(),
    })),
    defineUiEvent('ui-usage.anonymous.landing.link-clicked', LandingUiEventBase.extend({ page: LandingPageSchema, target: LandingLinkTargetSchema })),
    defineUiEvent('ui-usage.anonymous.landing.tour-feature-shown', LandingUiEventBase.extend({
        page: LandingPageSchema,
        feature: LandingTourFeatureSchema,
        how: z.enum(['autoplay', 'picked', 'stepped']),
    })),
];
